import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userPath } from '../../../shared/config/paths';
import { Card, Table, Button, Modal, Tag, Descriptions, message, Space, Upload, Form, Input, Select, Steps, Alert, Switch, Empty } from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import sslService from '../../services/sslService';
import { statusColor, statusLabel } from '../../../shared/constants/statusDictionary';
import { formatDate, formatDateTime } from '../../../shared/utils/date';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import RowActions, { useRowContextMenu } from '../../../shared/components/RowActions';

// 证书来源展示名（provider 是小写 slug）
const PROVIDER_LABELS = { letsencrypt: "Let's Encrypt", zerossl: 'ZeroSSL', gogetssl: 'GoGetSSL', sectigo: 'Sectigo', digicert: 'DigiCert', certum: 'Certum', manual: '手动上传', upload: '手动上传', buypass: 'Buypass', google: 'Google Trust Services' };

const { TextArea } = Input;
const { Option } = Select;

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

const IN_PROGRESS = ['pending', 'pending_validation', 'validating', 'processing', 'issuing'];

const MyCertificates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openOrder = (r) => navigate(userPath(r.category === 'code_signing' ? `/ssl/codesigning-order/${r.id}` : `/ssl/order/${r.id}`));
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [detailModal, setDetailModal] = useState({ visible: false, cert: null });
  const [uploadModal, setUploadModal] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCertificates();
  }, []);

  // 有进行中的证书(付款、验证、签发)时每 15 秒静默刷新;切回本标签页也刷新一次
  useEffect(() => {
    const reload = () => {
      if (!document.hidden) fetchCertificates(pagination.current, pagination.pageSize, true);
    };
    const busy = certificates.some((c) => IN_PROGRESS.includes(c.status));
    const timer = busy ? setInterval(reload, 15000) : null;
    document.addEventListener('visibilitychange', reload);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', reload);
    };
  }, [certificates, pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCertificates = async (page = 1, pageSize = 20, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await sslService.getCertificates(page, pageSize);
      if (response.success && response.data) {
        setCertificates(response.data.items || []);
        const pag = response.data.pagination || {};
        setPagination({
          current: pag.current_page || page,
          pageSize: pag.per_page || pageSize,
          total: pag.total || 0,
        });
      }
    } catch (error) {
      if (!silent) message.error(t('Failed to load certificates'));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleUploadManual = async (values) => {
    try {
      await sslService.uploadCertificate(values);
      message.success(t('Certificate uploaded'));
      setUploadModal(false);
      form.resetFields();
      fetchCertificates();
    } catch (error) {
      message.error(t('Upload failed: {{error}}', { error: error.message || t('Unknown error') }));
    }
  };

  const handleToggleAutoRenew = async (cert, value) => {
    try {
      const response = await sslService.toggleAutoRenew(cert.id, value);
      if (response.success) {
        message.success(response.auto_renew ? t('Auto-renewal enabled') : t('Auto-renewal disabled'));
        fetchCertificates();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      message.error(t('Operation failed: {{error}}', { error: error.message || t('Unknown error') }));
    }
  };

  const handleDelete = async (cert) => {
    try {
      const response = await sslService.deleteCertificate(cert.id);
      if (response.success) {
        message.success(t('Certificate deleted'));
        fetchCertificates(pagination.current, pagination.pageSize);
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      message.error(t('Deletion failed: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  const handleRenew = async (cert) => {
    try {
      const response = await sslService.renewCertificate(cert.id);
      if (response.success) {
        message.success(t('Renewal order created'));
        fetchCertificates(pagination.current, pagination.pageSize);
      } else {
        message.error(response.error || response.message || t('Renewal failed'));
      }
    } catch (error) {
      message.error(t('Renewal failed: {{error}}', { error: error.error || error.message || t('Unknown error') }));
    }
  };

  const handleDownload = (cert) => {
    // 创建下载链接
    const element = document.createElement('a');
    const file = new Blob([cert.certificate], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${cert.domain}.crt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      awaiting_configuration: 'blue',
      pending_validation: 'orange',
      validating: 'processing',
      failed: 'red',
      processing: 'blue',
      active: 'green',
      expired: 'red',
      expiring_soon: 'orange',
    };
    return colors[status] || statusColor(status, 'cert');
  };

  const getStatusText = (status, expiresAt) => {
    if (status === 'active' && expiresAt) {
      const daysLeft = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 30 && daysLeft > 0) {
        return t('Expiring soon ({{daysLeft}} days)', { daysLeft });
      }
    }

    const texts = {
      pending: t('Payment pending'),
      awaiting_configuration: t('Setup pending'),
      pending_validation: t('Verification pending'),
      validating: t('Verifying'),
      failed: t('Failed'),
      cancelled: t('Canceled'),
      processing: t('Processing'),
      active: t('Taking effect'),
      expired: t('Expired'),
      expiring_soon: t('Expiring soon'),
    };
    return texts[status] || t(statusLabel(status, 'cert'));
  };

  const getRowActions = (record) => [
    { key: 'open', label: record.status === 'awaiting_configuration' ? t('Configuration') : record.status === 'pending_validation' ? t('Verify') : t('Manage'), icon: <EyeOutlined />, inline: true, onClick: () => openOrder(record) },
    { key: 'download', label: t('Download'), icon: <DownloadOutlined />, inline: true, hidden: !record.certificate, onClick: () => handleDownload(record) },
    {
      key: 'delete', label: t('Delete'), icon: <DeleteOutlined />, danger: true,
      hidden: !(record.provider === 'manual' || ['pending', 'failed', 'expired', 'cancelled'].includes(record.status)),
      confirm: { title: t("Delete certificate"), content: t("This cannot be undone. Delete anyway?"), okText: t("Delete"), cancelText: t("Cancel"), okType: 'danger' },
      onClick: () => handleDelete(record),
    },
  ];
  const rowMenu = useRowContextMenu(getRowActions);

  const columns = [
    {
      title: t('Domains'),
      key: 'domain',
      render: (_, record) => (
        <div>
          <a style={{ fontWeight: 'bold' }} onClick={() => openOrder(record)}>{record.domain || record.product_name || t('Order #{{id}}', { id: record.id })}</a>
          {ensureArray(record.san_domains).length > 0 && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {t('+{{count}} additional domains', { count: ensureArray(record.san_domains).length })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('Type/brand'),
      key: 'type',
      render: (_, record) => (
        <div>
          {record.provider === 'gogetssl' && record.type === 'ACME' ? <Tag color="geekblue">ACME</Tag> : <Tag color="blue">{t(PROVIDER_LABELS[String(record.product?.type || record.provider || '').toLowerCase()] || record.product?.type || record.provider || '-')}</Tag>}
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {record.product?.brand || record.issuer || '-'}
          </div>
        </div>
      ),
    },
    {
      title: t('Status'),
      key: 'status',
      render: (_, record) => {
        const status = record.status;
        const expiresAt = record.expires_at;
        return (
          <Tag color={record.revoked_at ? 'red' : getStatusColor(status)}>
            {record.revoked_at ? t('Revoked') : getStatusText(status, expiresAt)}
          </Tag>
        );
      },
    },
    {
      title: t('Issued at'),
      dataIndex: 'issued_at',
      key: 'issued_at',
      render: (date) => formatDate(date),
    },
    {
      title: t('Expiration time'),
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date, record) => {
        const d = date || (record.type === 'ACME' ? record.paid_until : null); // ACME subscription: paid term
        return d ? new Date(d).toLocaleDateString('zh-CN') : '-';
      },
    },
    {
      title: t('Auto-renewal'),
      dataIndex: 'auto_renew',
      key: 'auto_renew',
      render: (autoRenew, record) => (
        <Switch
          checked={!!autoRenew}
          size="small"
          disabled={record.type === 'ACME'} // ACME subscription: managed on its page (stopping is irreversible)
          onChange={(value) => handleToggleAutoRenew(record, value)}
        />
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => <RowActions actions={getRowActions(record)} />,
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <SafetyOutlined />
            <span>{t('My certificates')}</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModal(true)}>
            {t('Upload certificate')}
          </Button>
        }
      >
        <Table
                    onRow={rowMenu.onRow(rowLink(navigate, (r) => (r.category === 'code_signing' ? `/ssl/codesigning-order/${r.id}` : `/ssl/order/${r.id}`)))}
                    rowClassName={() => ROW_LINK_CLASS}
          columns={columns}
          dataSource={certificates}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No SSL certificates yet. Upload a certificate in the top right or buy one from the certificate store.")}
              />
            ),
          }}
          onChange={(newPagination) => fetchCertificates(newPagination.current, newPagination.pageSize)}
        />
        {rowMenu.menu}
      </Card>

      {/* 证书详情Modal */}
      <Modal
        title={t("Certificate details")}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, cert: null })}
        footer={null}
        width={800}
      >
        {detailModal.cert && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t("Primary domain")}>{detailModal.cert.domain}</Descriptions.Item>
            <Descriptions.Item label={t("Status")}>
              <Tag color={getStatusColor(detailModal.cert.status)}>
                {getStatusText(detailModal.cert.status, detailModal.cert.expires_at)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("SAN domains")} span={2}>
              {ensureArray(detailModal.cert.san_domains).length > 0
                ? ensureArray(detailModal.cert.san_domains).join(', ')
                : t('None')}
            </Descriptions.Item>
            <Descriptions.Item label={t("Issuer")}>{detailModal.cert.issuer || '-'}</Descriptions.Item>
            <Descriptions.Item label={t("Serial number")}>{detailModal.cert.serial_number || '-'}</Descriptions.Item>
            <Descriptions.Item label={t("Issued at")}>
              {formatDateTime(detailModal.cert.issued_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t("Expiration time")}>
              {formatDateTime(detailModal.cert.expires_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t("SHA256 fingerprint")} span={2}>
              <code style={{ fontSize: '11px' }}>{detailModal.cert.fingerprint_sha256 || '-'}</code>
            </Descriptions.Item>
            <Descriptions.Item label={t("Auto-renewal")}>
              <Switch
                checked={!!detailModal.cert.auto_renew}
                size="small"
                onChange={(value) => handleToggleAutoRenew(detailModal.cert, value)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Provider">
              {detailModal.cert.provider}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 上传证书Modal */}
      <Modal
        title={t("Upload SSL certificate")}
        open={uploadModal}
        onCancel={() => {
          setUploadModal(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Alert
          message={t("Manually upload certificate")}
          description={t("If you already have a certificate from another CA, you can upload it here. Please include the full certificate chain and private key.")}
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form form={form} layout="vertical" onFinish={handleUploadManual}>
          <Form.Item name="domain" label={t("Primary domain")} rules={[{ required: true, message: t('Please enter a domain') }]}>
            <Input placeholder="example.com" />
          </Form.Item>
          <Form.Item name="certificate" label={t("Certificate content (PEM format)")} rules={[{ required: true, message: t('Enter a certificate') }]}>
            <TextArea rows={6} placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----" />
          </Form.Item>
          <Form.Item name="private_key" label={t("Private key (PEM format)")} rules={[{ required: true, message: t('Enter a private key') }]}>
            <TextArea rows={6} placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----" />
          </Form.Item>
          <Form.Item name="ca_bundle" label={t("CA Bundle (optional)")}>
            <TextArea rows={4} placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MyCertificates;
